/**
 * The two ways a lead route is climbed, and the one place the client works out
 * which of them somebody may choose.
 *
 * **Top rope is not a lesser lead.** It is a different thing to have done, and a
 * led route beats a top roped one however far up each climber got — so the style
 * is the first thing the server's ranking compares (`LeadRoundResult.compare`), not
 * a note on the card. The server's `LeadClimbStyle` is the same vocabulary.
 *
 * Who may do which is decided per round and per class: the youngest classes are
 * on top rope, the seniors lead, and the ones in between choose. One route can be
 * taken out of that choice (`required_climb_style`), which is the only thing a
 * route says about it.
 */

/** What a climber may have done, and what a route may insist on. */
export type LeadClimbStyle = 'top_rope' | 'lead';

/** What a class may be set to: one of the two, or the climber's own answer. */
export type LeadClassStyle = LeadClimbStyle | 'both';

/**
 * How each class climbs the round, by class id — the round's own
 * `category_styles`. A class the map does not mention leads, which is what every
 * lead card meant before the question existed.
 */
export type LeadClassStyles = Record<string, LeadClassStyle | undefined>;

/** Top rope first: it is the one a judge has to choose deliberately. */
export const LEAD_CLIMB_STYLES: LeadClimbStyle[] = ['top_rope', 'lead'];

/**
 * The styles somebody in `categoryId` may climb this route in.
 *
 * Two rules, in this order: **a route that insists leaves no choice**, and
 * otherwise the class's own setting stands. A climber with no class — or one the
 * round does not run — is read as leading, the same fallback the server makes.
 *
 * The route's forcing and the class's setting are deliberately put together here
 * and nowhere else: every surface that writes a card asks this, and the judge's
 * page, the panel's board and the athlete's own table must not disagree about
 * what a switch offers.
 */
export function permittedLeadStyles(
  route: { required_climb_style?: LeadClimbStyle | null } | null | undefined,
  categoryId: string | null | undefined,
  classStyles: LeadClassStyles | undefined
): LeadClimbStyle[] {
  if (route?.required_climb_style) return [route.required_climb_style];

  const setting = (categoryId && classStyles?.[categoryId]) || 'lead';
  if (setting === 'both') return [...LEAD_CLIMB_STYLES];
  return [setting];
}

/** The i18n key for a style's label, which is all the copy there is for one. */
export function leadStyleLabelKey(style: LeadClimbStyle): string {
  return `leadRounds.style.${style}`;
}

/**
 * The short label a card is read back with — "TR" next to a number, so a column
 * of fifty cards stays a column. The long one names it in full.
 */
export function leadStyleShortKey(style: LeadClimbStyle): string {
  return `leadRounds.style.short.${style}`;
}
